import React from 'react';

const ARCHITECTURE_STEPS = [
  { step: '01', title: 'Client UI Layer', desc: 'React, TypeScript, TailwindCSS, WebRTC Video', tag: 'Frontend' },
  { step: '02', title: 'API & Microservices', desc: 'FastAPI, Node.js, REST & GraphQL Endpoints', tag: 'Backend Engine' },
  { step: '03', title: 'AI & Machine Learning', desc: 'Claude AI, Vision Document Extraction, LLM Workflows', tag: 'Intelligence' },
  { step: '04', title: 'Database & Storage', desc: 'PostgreSQL, Redis Caching, S3 Object Storage', tag: 'Persistence' },
  { step: '05', title: 'Cloud Infrastructure', desc: 'AWS (ECS/EKS/Lambda), Azure, GCP, Terraform IaC', tag: 'Cloud Native' },
  { step: '06', title: 'DevOps & Monitoring', desc: 'GitHub Actions CI/CD, Docker, Grafana, OpenTelemetry', tag: 'Operations' },
];

const ArchitectureVisualizer: React.FC = () => {
  return (
    <section id="architecture" className="py-24 bg-slate-900 text-white relative">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-3xl mb-16">
          <p className="text-blue-400 text-sm font-semibold uppercase tracking-widest mb-3">
            Engineering Rigor & Architecture
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-4">
            Cloud-Native Architecture <span className="text-blue-400">By Default</span>
          </h2>
          <p className="text-lg text-slate-300 leading-relaxed">
            Every system built by CloudIgnite is designed for zero single points of failure, automated CI/CD deployment pipelines, and maximum cost efficiency.
          </p>
        </div>

        {/* Visual Pipeline Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {ARCHITECTURE_STEPS.map((s) => (
            <div
              key={s.step}
              className="bg-slate-800/60 border border-slate-700/80 rounded-2xl p-6 relative hover:border-blue-500/60 transition-all duration-300"
            >
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-mono font-bold text-blue-400 px-2.5 py-1 bg-blue-500/10 rounded-lg border border-blue-500/20">
                  LAYER {s.step}
                </span>
                <span className="text-xs font-medium text-slate-400 bg-slate-900 px-2.5 py-0.5 rounded-full">
                  {s.tag}
                </span>
              </div>
              <h3 className="text-lg font-bold text-white mb-2">{s.title}</h3>
              <p className="text-xs text-slate-300 font-mono bg-slate-950 p-3 rounded-xl border border-slate-800/80">
                {s.desc}
              </p>
            </div>
          ))}
        </div>

        {/* Bottom Proof Checklist */}
        <div className="bg-slate-950 border border-slate-800 rounded-2xl p-6 sm:p-8 grid sm:grid-cols-3 gap-6">
          <div className="flex items-start gap-3">
            <span className="text-emerald-400 text-xl font-bold">✓</span>
            <div>
              <h4 className="text-sm font-bold text-white mb-1">Automated CI/CD Pipelines</h4>
              <p className="text-xs text-slate-400 leading-relaxed">Every commit is automatically tested, linted, and deployed with GitHub Actions.</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <span className="text-emerald-400 text-xl font-bold">✓</span>
            <div>
              <h4 className="text-sm font-bold text-white mb-1">Infrastructure as Code (IaC)</h4>
              <p className="text-xs text-slate-400 leading-relaxed">Provisioned via Terraform and Pulumi. Fully reproducible, auditable cloud environments.</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <span className="text-emerald-400 text-xl font-bold">✓</span>
            <div>
              <h4 className="text-sm font-bold text-white mb-1">FinOps & Cost Controls</h4>
              <p className="text-xs text-slate-400 leading-relaxed">Automated cloud cost tracking and resource right-sizing to eliminate waste.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ArchitectureVisualizer;
